import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { SNAKE_CONFIG } from './snakeConfig.js';

const PERK_LABEL_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 10, fontWeight: 'bold', fill: '#ffffff',
});

/**
 * Big chunky number rendered ABOVE the perk icon during its last few
 * seconds (5, 4, 3, 2, 1). Heavy black stroke so it reads against any
 * board background. Lives as a sibling of the icon container so the
 * icon's pulse scaling does NOT scale the number — it stays rock-steady
 * and easy to read while the icon underneath wobbles.
 */
const PERK_COUNTDOWN_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 26,
    fontWeight: '900',
    fill: '#ffffff',
    stroke: { color: '#0f172a', width: 5, join: 'round' },
});

/**
 * Default countdown window — overridable per-instance via the
 * constructor. Mirrors `PERK_BLINK_MS` so the on-icon `5..1` digits
 * appear during the same red-phase the existing wobble uses.
 */
const DEFAULT_PERK_COUNTDOWN_WINDOW_MS = 5000;

const {
    PERK_SPAWN_INTERVAL_MS,
    PERK_LIFETIME_MS,
    PERK_BLINK_MS,
    PERK_MAX_ON_BOARD,
    PERK_RENDER_SCALE,
    PERK_TYPES,
} = SNAKE_CONFIG;

/**
 * Manages power-up / perk spawning, expiry, collection, and rendering.
 * Perks appear at random intervals, pulse to draw attention, and blink
 * fast when about to vanish.
 */
export class SnakePerks {
    container = new Container();

    /** @type {{ col: number, row: number, type: object, expiresAt: number, gfx: Graphics }[]} */
    _perks = [];

    /** Timestamp for the next spawn attempt */
    _nextSpawnAt = 0;

    _themeManager = null;

    /**
     * @param {*} themeManager
     * @param {{ lifetimeMs?: number, blinkMs?: number, countdownWindowMs?: number }} [opts]
     *   Per-instance overrides for the perk timing constants.
     *   `lifetimeMs` — total on-board time before the perk expires.
     *   `blinkMs`    — how long the warning red-phase wobble lasts at
     *                  the end of life.
     *   `countdownWindowMs` — when the big 5..1 digit appears above the
     *                  icon. Defaults to `blinkMs` so the digit and the
     *                  red-phase wobble line up; can be overridden if
     *                  you want the number to appear earlier.
     *   When omitted, falls back to the live `SNAKE_CONFIG` values.
     */
    constructor(themeManager = null, opts = {}) {
        this._themeManager = themeManager;
        this._lifetimeMs = Number.isFinite(opts.lifetimeMs)
            ? opts.lifetimeMs
            : PERK_LIFETIME_MS;
        this._blinkMs = Number.isFinite(opts.blinkMs)
            ? opts.blinkMs
            : PERK_BLINK_MS;
        // Default the on-icon 5..1 digit window to the blink window so
        // the number appears in lockstep with the red-phase wobble.
        // The legacy hard-coded 5000ms is retained as the floor only
        // when no blink override is given AND the static fallback is
        // larger — keeps existing behaviour unchanged for callers that
        // pass nothing.
        this._countdownWindowMs = Number.isFinite(opts.countdownWindowMs)
            ? opts.countdownWindowMs
            : (Number.isFinite(opts.blinkMs)
                ? opts.blinkMs
                : DEFAULT_PERK_COUNTDOWN_WINDOW_MS);
    }

    init() {
        this._scheduleNextSpawn();
    }

    /**
     * Called once per game tick. Handles expiry and spawning.
     * @param {number} cols
     * @param {number} rows
     * @param {number} ox  — grid originX
     * @param {number} oy  — grid originY
     * @param {number} cs  — cellSize
     * @param {(c: number, r: number) => boolean} isOccupied
     */
    gameTick(cols, rows, ox, oy, cs, isOccupied) {
        const now = Date.now();

        for (let i = this._perks.length - 1; i >= 0; i--) {
            if (now >= this._perks[i].expiresAt) {
                this._removePerk(i);
            }
        }

        if (now >= this._nextSpawnAt && this._perks.length < PERK_MAX_ON_BOARD) {
            const pos = this._findEmpty(cols, rows, isOccupied);
            if (pos) this._spawn(pos.col, pos.row, ox, oy, cs);
            this._scheduleNextSpawn();
        }
    }

    /**
     * Check if head is on a perk cell. Returns the perk type object or null.
     * Removes the collected perk from the board.
     */
    checkCollection(col, row) {
        for (let i = 0; i < this._perks.length; i++) {
            if (this._perks[i].col === col && this._perks[i].row === row) {
                const type = this._perks[i].type;
                this._removePerk(i);
                return type;
            }
        }
        return null;
    }

    /** @returns {boolean} */
    occupies(col, row) {
        return this._perks.some(p => p.col === col && p.row === row);
    }

    /**
     * Animate all active perk icons (call every frame in update).
     *
     * Icon `alpha` is never touched — the pulse stays fully opaque. The
     * "alive" feel comes from:
     *   • a very gentle scale wobble on the icon container,
     *   • a handful of tiny `+`-shaped twinkle marks scattered ON the
     *     icon that each independently rotate and pulse on/off, so the
     *     icon reads like a polished shiny object catching the light,
     *   • a slightly more emphatic (but still calm) wobble in the last
     *     PERK_BLINK_MS to underscore the warning, while a chunky 5-4-3-
     *     2-1 countdown number above the icon does the heavy lifting of
     *     telling the player exactly how long they have left.
     *
     * Amplitudes are deliberately small — earlier versions snapped the
     * icon between scale 0.4 and 1.1 in the final seconds, which made
     * the whole board feel jittery and unreadable.
     */
    pulse() {
        const now = Date.now();
        for (const perk of this._perks) {
            const remaining = perk.expiresAt - now;

            // Gentle, slow "breathing" wobble at all times. In the last
            // PERK_BLINK_MS the wobble speeds up slightly and widens a
            // hair, but still stays well inside ±8% of full size.
            let scale;
            if (remaining < this._blinkMs) {
                scale = 0.94 + 0.06 * Math.sin(now * 0.012);
            } else {
                scale = 0.97 + 0.03 * Math.sin(now * 0.004);
            }
            perk.gfx.scale.set(scale);
            perk.gfx.alpha = 1;

            // Countdown number — only visible in the last
            // PERK_COUNTDOWN_WINDOW_MS (5..1). Lives outside the gfx
            // container so the wobble above does NOT scale the digits.
            if (perk.countdown) {
                if (remaining > 0 && remaining < this._countdownWindowMs) {
                    const secs = Math.max(1, Math.ceil(remaining / 1000));
                    const str = String(secs);
                    if (perk.countdown.text !== str) perk.countdown.text = str;
                    perk.countdown.visible = true;
                } else {
                    perk.countdown.visible = false;
                }
            }

            // Per-twinkle: own rotation around its anchor, own alpha
            // pulse with a phase offset so they wink in/out sequentially.
            if (perk.twinkles) {
                for (const tw of perk.twinkles) {
                    tw.gfx.rotation = now * tw.rotSpeed;
                    const t = (Math.sin(now * 0.005 + tw.phase) + 1) * 0.5; // 0..1
                    tw.gfx.alpha = 0.25 + 0.75 * t;
                }
            }
        }
    }

    /** Remove all perks from the board (used on board transition / restart). */
    clearAll() {
        while (this._perks.length > 0) this._removePerk(0);
        this._scheduleNextSpawn();
    }

    destroy() {
        this.clearAll();
        this.container.removeChildren();
    }

    // ── internals ──────────────────────────────────────

    _spawn(col, row, ox, oy, cs) {
        const type = PERK_TYPES[Math.floor(Math.random() * PERK_TYPES.length)];
        // Visual size grows with PERK_RENDER_SCALE — the pickup still lives in
        // one cell, but the icon overflows visually so it's easier to spot.
        const renderSize = (cs - 8) * (PERK_RENDER_SCALE || 1);
        const r = renderSize / 2;
        const slotKey = `snake.perk.${type.id}`;
        const sprite = this._themeManager?.createSprite(slotKey, renderSize, renderSize);
        const gfx = sprite ? new Container() : new Graphics();
        let twinkles = null;

        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            gfx.addChild(sprite);
            // Little `+`-shaped twinkle marks ON the icon — each rotates
            // around its own anchor and winks in/out on its own phase,
            // so the icon reads like a polished shiny object.
            twinkles = this._attachTwinkles(gfx, r);
        } else {
            switch (type.shape) {
                case 'triangle_down':
                    gfx.moveTo(0, r);
                    gfx.lineTo(-r, -r);
                    gfx.lineTo(r, -r);
                    gfx.closePath();
                    gfx.fill(type.color);
                    break;

                case 'triangle_up':
                    gfx.moveTo(0, -r);
                    gfx.lineTo(-r, r);
                    gfx.lineTo(r, r);
                    gfx.closePath();
                    gfx.fill(type.color);
                    break;

                case 'diamond':
                    gfx.moveTo(0, -r);
                    gfx.lineTo(r, 0);
                    gfx.lineTo(0, r);
                    gfx.lineTo(-r, 0);
                    gfx.closePath();
                    gfx.fill(type.color);
                    break;

                case 'ring':
                    gfx.circle(0, 0, r);
                    gfx.stroke({ color: type.color, width: 4 });
                    gfx.circle(0, 0, r * 0.4);
                    gfx.fill(type.color);
                    break;

                case 'square':
                    gfx.roundRect(-r, -r, r * 2, r * 2, 6);
                    gfx.fill(type.color);
                    break;
            }
        }

        gfx.x = ox + col * cs + cs / 2;
        gfx.y = oy + row * cs + cs / 2;

        if (!sprite) {
            const label = new Text({ text: type.label, style: PERK_LABEL_STYLE });
            label.anchor.set(0.5);
            label.y = r + 10;
            gfx.addChild(label);
            // Twinkles for the fallback geometric shapes too, so every
            // perk feels alive whether or not a PNG has been uploaded.
            twinkles = this._attachTwinkles(gfx, r);
        }

        // Countdown digit ("5".."1") shown above the icon during the
        // last PERK_COUNTDOWN_WINDOW_MS. Lives as a SIBLING of the icon
        // (not a child) so the icon's pulse scaling does not warp the
        // digits — they stay sharp and steady while the icon wobbles.
        const countdown = new Text({ text: '', style: PERK_COUNTDOWN_STYLE });
        countdown.anchor.set(0.5);
        countdown.x = gfx.x;
        countdown.y = gfx.y - cs * 0.85;
        countdown.visible = false;

        this._perks.push({
            col, row, type,
            expiresAt: Date.now() + this._lifetimeMs,
            gfx,
            twinkles,
            countdown,
        });
        this.container.addChild(gfx);
        this.container.addChild(countdown);
    }

    /**
     * Build and attach a handful of small `+`-shaped twinkle marks ON
     * the icon. Each twinkle is its own `Graphics` so it can be rotated
     * and faded independently in {@link pulse}. Marks are intentionally
     * placed *inside* the icon's radius (not on a halo ring) and use
     * additive blending so they read as classic shine highlights.
     *
     * @returns {Array<{ gfx: import('../../../vendor/pixi.min.mjs').Graphics, phase: number, rotSpeed: number }>}
     *   list shared with {@link pulse} for per-frame animation.
     */
    _attachTwinkles(parent, iconRadius) {
        // Each tuple = (xRatio, yRatio, sizeRatio, phaseOffset).
        // xRatio/yRatio are anchor points inside the icon (|·| < 1).
        // sizeRatio = arm length of the `+` relative to iconRadius.
        const SPECS = [
            { fx:  0.40, fy: -0.32, fs: 0.22, phase: 0.0  },
            { fx: -0.42, fy:  0.10, fs: 0.18, phase: 1.7  },
            { fx:  0.18, fy:  0.42, fs: 0.16, phase: 3.1  },
        ];

        const twinkles = [];
        for (const spec of SPECS) {
            const armLen = Math.max(3, iconRadius * spec.fs);
            const thick  = Math.max(1.5, armLen * 0.32);

            const g = new Graphics();
            // `+` shape, centred on its own origin so .rotation pivots
            // around the twinkle anchor (not the icon centre).
            g.moveTo(0, -armLen);
            g.lineTo(0,  armLen);
            g.moveTo(-armLen, 0);
            g.lineTo( armLen, 0);
            g.stroke({ color: 0xffffff, width: thick, alpha: 1, cap: 'round' });

            g.x = iconRadius * spec.fx;
            g.y = iconRadius * spec.fy;
            g.blendMode = 'add';
            parent.addChild(g);

            twinkles.push({
                gfx: g,
                phase: spec.phase,
                // Slow, slightly varied rotation per twinkle so they
                // don't lockstep.
                rotSpeed: 0.0008 + Math.random() * 0.0008,
            });
        }
        return twinkles;
    }

    _removePerk(index) {
        const perk = this._perks[index];
        if (perk.gfx.parent) perk.gfx.parent.removeChild(perk.gfx);
        // children:true so the icon sprite *and* the twinkle marks
        // inside the container are torn down with their parent.
        perk.gfx.destroy({ children: true });
        if (perk.countdown) {
            if (perk.countdown.parent) perk.countdown.parent.removeChild(perk.countdown);
            perk.countdown.destroy();
        }
        this._perks.splice(index, 1);
    }

    _findEmpty(cols, rows, isOccupied) {
        const candidates = [];
        for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
                if (!isOccupied(c, r)) candidates.push({ col: c, row: r });
            }
        }
        if (candidates.length === 0) return null;
        return candidates[Math.floor(Math.random() * candidates.length)];
    }

    _scheduleNextSpawn() {
        const [min, max] = PERK_SPAWN_INTERVAL_MS;
        this._nextSpawnAt = Date.now() + min + Math.floor(Math.random() * (max - min + 1));
    }
}
